import { VOIVODESHIPS } from '@ooh/contracts';

/**
 * Strips diacritics and lowercases a string for fuzzy voivodeship matching.
 * Explicitly maps ł→l because U+0142 does not decompose under NFD.
 */
function stripAndLower(str: string): string {
  return str
    .toLowerCase()
    .replace(/ł/g, 'l') // ł has no NFD decomposition — must be replaced explicitly
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Lookup map from normalized form → canonical VOIVODESHIPS entry.
 * Built once at module load. Key = stripAndLower(v.value) which equals the value
 * itself (values are already ASCII-lowercase). Matching normalizes the raw DB string
 * so variants like "Mazowieckie", "małopolskie", "Łódzkie" all resolve correctly.
 */
const VOIVODESHIP_BY_NORMALIZED: Map<string, (typeof VOIVODESHIPS)[number]> = new Map(
  VOIVODESHIPS.map((v) => [stripAndLower(v.value), v]),
);

/**
 * Maps raw API region strings to canonical display options.
 *
 * Shared between the catalog (`features/media-units/lib/filter-fields.ts`) and
 * the offer units-tab (`features/offers/units-tab/lib/filter-fields.ts`) — both
 * feed the combined "Lokalizacja" pill's region group and must agree on
 * voivodeship normalization to stay catalog-parity.
 *
 * - Recognized voivodeship codes/labels → canonical { value, label } from VOIVODESHIPS.
 * - Variant casings / diacritics of the same voivodeship are deduplicated (first seen wins).
 * - Unrecognized values (city names, foreign entries) → included as fallback with
 *   their raw value preserved for filter compatibility. `formatFallbackLabel` controls
 *   the display label for this fallback case (identity by default; the catalog passes
 *   its `capitalize` helper to keep its pre-existing display behavior).
 * - Result is sorted by label using Polish locale.
 */
export function buildRegionOptions(
  regions: string[],
  formatFallbackLabel: (raw: string) => string = (raw) => raw,
): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  const options: Array<{ value: string; label: string }> = [];

  for (const raw of regions) {
    const normalized = stripAndLower(raw);
    const canonical = VOIVODESHIP_BY_NORMALIZED.get(normalized);

    if (canonical) {
      // Deduplicate: multiple raw variants that resolve to the same voivodeship
      // (e.g. "mazowieckie" + "Mazowieckie") collapse into one canonical entry.
      if (!seen.has(canonical.value)) {
        seen.add(canonical.value);
        options.push({ value: canonical.value, label: canonical.label });
      }
    } else {
      // Unknown value — keep raw as filter value, format as display label.
      if (!seen.has(raw)) {
        seen.add(raw);
        options.push({ value: raw, label: formatFallbackLabel(raw) });
      }
    }
  }

  return options.sort((a, b) => a.label.localeCompare(b.label, 'pl'));
}
